import imageCompression from 'browser-image-compression';
import { supabase } from '@lib/supabase/client';

// Client-side image compression + upload pipeline shared by every
// MarkdownComposer (forum, strain reviews, blog).
//
// Why client-side compression?
//   * Saves bandwidth from mobile patients on metered connections.
//   * Keeps us under the 2 MB-per-object bucket cap defined in
//     migration 0046 — original phone photos routinely come in at 4–8 MB.
//   * WebP @ 1200px / q=0.85 is enough resolution to read a printed
//     batch label or inspect trichome density, without paying for
//     full-res storage.
//
// Why two buckets, not one with row-level filtering?
//   * Public bucket reads go through Supabase's CDN edge with no auth
//     check at all — we get the latency of a static asset for blog
//     covers and inline blog images.
//   * Private bucket reads require a signed URL produced server-side
//     by the SecureImage component, so no anon scraper can enumerate
//     forum image ids, and a leaked URL expires inside the hour.
//
// Path scheme: every upload (private OR public) goes under
// `${user.id}/${uuid}.webp`. Private bucket RLS enforces the
// uid-prefix; public bucket doesn't need it but we apply the same
// scheme so admin auditing has a consistent shape across both.

const MAX_RAW_BYTES = 5 * 1024 * 1024;

// `private:<bucket-relative path>` is the reference we embed in
// markdown text for private uploads. It is NOT a URL — the SecureImage
// component recognises the prefix and produces a fresh signed URL on
// every render. Storing the long-lived path instead of an expiring
// signed URL is the only reason private images survive past an hour.
export const PRIVATE_REF_PREFIX = 'private:';

export type ImageBucket = 'public_images' | 'private_images';

export type UploadResult = {
  /** Reference to splice into markdown source. */
  reference: string;
  /** Bucket-relative path (`<userId>/<uuid>.webp`). */
  path: string;
  bucket: ImageBucket;
};

export class ImageUploadError extends Error {
  override readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ImageUploadError';
    this.cause = cause;
  }
}

export async function compressAndUpload(
  file: File,
  opts: { isPrivate: boolean },
): Promise<UploadResult> {
  // Pre-flight checks. Bail before we spin up the compression worker
  // for obviously-wrong inputs — saves the ~6 MB worker bundle on
  // misuse paths.
  if (!file.type.startsWith('image/')) {
    throw new ImageUploadError('Only image files are supported.');
  }
  if (file.size > MAX_RAW_BYTES) {
    throw new ImageUploadError(
      'Image is larger than 5 MB. Pick a smaller file or crop in your camera roll first.',
    );
  }

  // browser-image-compression infers the output format from `fileType`.
  // Setting it explicitly to `image/webp` guarantees the bucket's
  // MIME allow-list (image/webp only) accepts the upload — without
  // this the library would emit the source MIME and storage RLS
  // would reject it.
  let compressed: File | Blob;
  try {
    compressed = await imageCompression(file, {
      maxWidthOrHeight: 1200,
      fileType: 'image/webp',
      initialQuality: 0.85,
      useWebWorker: true,
    });
  } catch (err) {
    throw new ImageUploadError('Could not compress this image.', err);
  }

  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData.user) {
    throw new ImageUploadError('You must be signed in to upload images.', userError);
  }

  const bucket: ImageBucket = opts.isPrivate ? 'private_images' : 'public_images';
  const path = `${userData.user.id}/${crypto.randomUUID()}.webp`;

  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(path, compressed, {
      contentType: 'image/webp',
      // Year-long cache — paths are content-addressed (uuid) so
      // immutability is fine. Public CDN benefits especially.
      cacheControl: '31536000',
      upsert: false,
    });

  if (uploadError) {
    throw new ImageUploadError(
      `Upload failed: ${uploadError.message}`,
      uploadError,
    );
  }

  if (opts.isPrivate) {
    return { reference: `${PRIVATE_REF_PREFIX}${path}`, path, bucket };
  }

  // Public bucket: the public URL is stable and CDN-fronted, so we can
  // splice it straight into markdown text without further indirection.
  const { data: publicData } = supabase.storage
    .from('public_images')
    .getPublicUrl(path);
  return { reference: publicData.publicUrl, path, bucket };
}

export function isPrivateImageRef(ref: string): boolean {
  return ref.startsWith(PRIVATE_REF_PREFIX);
}

export function privateRefToPath(ref: string): string {
  return ref.startsWith(PRIVATE_REF_PREFIX)
    ? ref.slice(PRIVATE_REF_PREFIX.length)
    : ref;
}

// Matches the markdown image syntax produced by MarkdownComposer's
// upload flow: `![alt](private:<bucket-path>)`. The capture group
// returns the full `private:<path>` reference. The trailing `[^)\s]+`
// stops at whitespace to avoid swallowing a trailing space that
// followed a malformed paste.
const PRIVATE_IMAGE_MD_RE = /!\[[^\]]*\]\((private:[^)\s]+)\)/g;

// Pull out every `private:<path>` reference embedded as a markdown
// image in `markdown`. De-duped (a user pasting the same image
// twice should still get a single tile in the gallery), preserving
// first-seen order so the gallery's hero matches the prose order.
export function extractPrivateImageRefs(markdown: string): string[] {
  if (!markdown) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  // Reset lastIndex defensively in case a previous caller threw mid-iter.
  PRIVATE_IMAGE_MD_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PRIVATE_IMAGE_MD_RE.exec(markdown)) !== null) {
    const ref = m[1];
    if (ref && !seen.has(ref)) {
      seen.add(ref);
      out.push(ref);
    }
  }
  return out;
}

// Strip every `![alt](private:<path>)` token from a markdown string.
// Used by the review form so the prose stored in `other_notes` and
// composed into the forum-post body doesn't carry inline image
// syntax — the gallery owns image display, the notes own prose.
// Collapses runs of 3+ newlines (left over after stripping) back
// down to a paragraph break, and trims edges.
export function stripPrivateImageRefs(markdown: string): string {
  if (!markdown) return '';
  return markdown
    .replace(PRIVATE_IMAGE_MD_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
